import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, BookOpen, Settings, CheckCircle, XCircle, Loader2, Plus, Trash2, Upload, UserPlus, Shield, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';
import EmpleadosTab from '../components/EmpleadosTab';
import ResultadosTab from '../components/ResultadosTab';
import FacturacionTab from '../components/FacturacionTab';
import CMSTab from '../components/CMSTab';
import WhatsAppAnalyticsTab from '../components/WhatsAppAnalyticsTab';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [estudios, setEstudios] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmpleadoForm, setShowEmpleadoForm] = useState(false);
  const [showResultadoForm, setShowResultadoForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [nuevoEmpleado, setNuevoEmpleado] = useState({
    nombre: '',
    email: '',
    password: '',
    telefono: '',
    permisos: {
      puede_aprobar_veterinarios: false,
      puede_aprobar_posts: false,
      puede_subir_pdfs: false,
      puede_gestionar_empleados: false
    }
  });
  const [resultadoData, setResultadoData] = useState({
    veterinario_email: '',
    paciente_nombre: '',
    estudio_nombre: '',
    notas: '',
    file: null
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    const isAdmin = localStorage.getItem('userAdmin') === 'true';
    if (!isAdmin) {
      toast({
        title: 'Acceso denegado',
        description: 'No tienes permisos de administrador',
        variant: 'destructive',
      });
      navigate('/');
      return;
    }
    loadData();
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, pendingResponse, estudiosResponse, pendingPostsResponse, publishedPostsResponse, empleadosResponse] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/users/pending'),
        api.get('/admin/estudios'),
        api.get('/blog/admin/pending'),
        api.get('/blog/posts/published'),
        api.get('/empleados/')
      ]);
      setAllUsers(usersResponse.data);
      setPendingUsers(pendingResponse.data);
      setEstudios(estudiosResponse.data);
      setPendingPosts(pendingPostsResponse.data);
      setPublishedPosts(publishedPostsResponse.data);
      setEmpleados(empleadosResponse.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId, canCreateBlog = false) => {
    try {
      await api.post(`/admin/users/${userId}/approve`, {
        can_create_blog: canCreateBlog
      });
      toast({
        title: '✅ Usuario aprobado',
        description: 'El veterinario puede ahora realizar compras' + (canCreateBlog ? ' y crear posts' : ''),
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo aprobar el usuario',
        variant: 'destructive',
      });
    }
  };

  const rejectUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de rechazar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/reject`);
      toast({
        title: 'Usuario rechazado',
        description: 'La cuenta ha sido eliminada',
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo rechazar el usuario',
        variant: 'destructive',
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: 'Error',
        description: 'Las contraseñas no coinciden',
        variant: 'destructive',
      });
      return;
    }

    try {
      await api.post('/admin/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast({
        title: '✅ Contraseña actualizada',
        description: 'Tu contraseña ha sido cambiada exitosamente',
      });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo cambiar la contraseña',
        variant: 'destructive',
      });
    }
  };

  const handleResetUserPassword = async (userId) => {
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      toast({
        title: 'Error',
        description: 'La contraseña debe tener al menos 6 caracteres',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await api.post(`/admin/users/${userId}/reset-password`, {
        new_password: resetPasswordValue
      });
      toast({
        title: '✅ Contraseña actualizada',
        description: response.data.message,
      });
      setResetPasswordUserId(null);
      setResetPasswordValue('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo resetear la contraseña',
        variant: 'destructive',
      });
    }
  };

  const approvePost = async (postId) => {
    try {
      await api.post(`/blog/admin/${postId}/approve`);
      toast({
        title: '✅ Post aprobado',
        description: 'El post ha sido publicado en el blog',
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo aprobar el post',
        variant: 'destructive',
      });
    }
  };

  const rejectPost = async (postId) => {
    if (!window.confirm('¿Estás seguro de rechazar este post?')) {
      return;
    }
    try {
      await api.post(`/blog/admin/${postId}/reject`);
      toast({
        title: 'Post rechazado',
        description: 'El post no será publicado',
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo rechazar el post',
        variant: 'destructive',
      });
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('¿Estás seguro de eliminar este post? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      await api.delete(`/blog/admin/${postId}`);
      toast({
        title: 'Post eliminado',
        description: 'El post ha sido eliminado',
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el post',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e40af]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-2">🔧 Panel de Administración</h1>
          <p className="text-lg opacity-90">MoraBioVet - Gestión del sistema</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-9 mb-8 lg:w-auto lg:inline-grid">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Usuarios
              {pendingUsers.length > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingUsers.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="estudios" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Estudios
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Blog
            </TabsTrigger>
            <TabsTrigger value="empleados" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Empleados
            </TabsTrigger>
            <TabsTrigger value="resultados" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Resultados
            </TabsTrigger>
            <TabsTrigger value="facturacion" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Facturación
            </TabsTrigger>
            <TabsTrigger value="cms" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              CMS
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2" data-testid="tab-whatsapp">
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Config
            </TabsTrigger>
          </TabsList>

          {/* Tab: Usuarios Pendientes */}
          <TabsContent value="users">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">
                  ⏳ Usuarios Pendientes de Aprobación ({pendingUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingUsers.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay usuarios pendientes</p>
                ) : (
                  <div className="space-y-4">
                    {pendingUsers.map((user) => (
                      <Card key={user.id} className="border-2 border-yellow-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-[#1e3a8a]">{user.nombre}</h3>
                              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                <div>
                                  <span className="text-gray-500">Email:</span>
                                  <span className="ml-2 font-medium">{user.email}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Teléfono:</span>
                                  <span className="ml-2 font-medium">{user.telefono}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Nombre completo:</span>
                                  <span className="ml-2 font-medium">{user.nombre_completo_cedula}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Cédula Profesional:</span>
                                  <span className="ml-2 font-medium text-[#1e40af]">{user.cedula_profesional}</span>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                Registrado: {new Date(user.created_at).toLocaleString('es-MX')}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              <div className="flex items-center gap-2 mb-2">
                                <input
                                  type="checkbox"
                                  id={`blog-${user.id}`}
                                  className="w-4 h-4"
                                />
                                <label htmlFor={`blog-${user.id}`} className="text-sm">
                                  ✍️ Puede crear posts de blog
                                </label>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => {
                                    const checkbox = document.getElementById(`blog-${user.id}`);
                                    approveUser(user.id, checkbox.checked);
                                  }}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Aprobar
                                </Button>
                                <Button
                                  onClick={() => rejectUser(user.id)}
                                  variant="destructive"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Rechazar
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Todos los usuarios */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">
                  👥 Todos los Usuarios ({allUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">Nombre</th>
                        <th className="p-2 text-left">Email</th>
                        <th className="p-2 text-left">Cédula</th>
                        <th className="p-2 text-center">Estado</th>
                        <th className="p-2 text-center">Admin</th>
                        <th className="p-2 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map((user) => (
                        <tr key={user.id} className="border-b">
                          <td className="p-2">{user.nombre}</td>
                          <td className="p-2">{user.email}</td>
                          <td className="p-2">{user.cedula_profesional}</td>
                          <td className="p-2 text-center">
                            {user.is_approved ? (
                              <span className="text-green-600 font-semibold">✓ Aprobado</span>
                            ) : (
                              <span className="text-yellow-600 font-semibold">⏳ Pendiente</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {user.is_admin && <span className="text-blue-600 font-semibold">Admin</span>}
                          </td>
                          <td className="p-2 text-center">
                            {resetPasswordUserId === user.id ? (
                              <div className="flex items-center gap-2 justify-center">
                                <Input
                                  type="text"
                                  placeholder="Nueva contraseña (mín. 6 caracteres)"
                                  value={resetPasswordValue}
                                  onChange={(e) => setResetPasswordValue(e.target.value)}
                                  className="w-48"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleResetUserPassword(user.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                  title="Guardar nueva contraseña"
                                >
                                  ✓ Guardar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setResetPasswordUserId(null);
                                    setResetPasswordValue('');
                                  }}
                                  title="Cancelar"
                                >
                                  ✗
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setResetPasswordUserId(user.id)}
                                className="text-[#1e40af] border-[#1e40af] hover:bg-[#1e40af] hover:text-white"
                              >
                                🔑 Asignar Contraseña
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Estudios */}
          <TabsContent value="estudios">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-[#1e3a8a]">📋 Gestión de Estudios</CardTitle>
                  <p className="text-sm text-gray-600">Total de estudios: {estudios.length}</p>
                </div>
                <Button
                  onClick={() => window.open('/admin/estudio/nuevo', '_blank')}
                  className="bg-[#84cc16] hover:bg-[#65a30d]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Estudio
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {estudios.map((estudio) => (
                    <Card key={estudio.id} className="border-2 hover:border-[#84cc16] transition-colors">
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg text-[#1e3a8a] mb-2">{estudio.nombre}</h3>
                        <div className="space-y-1 text-sm mb-3">
                          <p><span className="font-semibold">Categoría:</span> {estudio.categoria}</p>
                          <p><span className="font-semibold">Precio:</span> ${estudio.precio} MXN</p>
                          <p><span className="font-semibold">Entrega:</span> {estudio.tiempo_entrega}</p>
                        </div>
                        <Button
                          onClick={() => {
                            const editUrl = `/admin/estudio/${estudio.id}`;
                            window.open(editUrl, '_blank');
                          }}
                          className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                          size="sm"
                        >
                          ✏️ Editar Detalles
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Blog */}
          <TabsContent value="blog">
            <div className="space-y-6">
              {/* Posts Pendientes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">
                    ⏳ Posts Pendientes de Aprobación ({pendingPosts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingPosts.length === 0 ? (
                    <p className="text-center py-8 text-gray-500">No hay posts pendientes</p>
                  ) : (
                    <div className="space-y-4">
                      {pendingPosts.map((post) => (
                        <Card key={post.id} className="border-2 border-yellow-200">
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">{post.titulo}</h3>
                                <p className="text-gray-600 mb-3">{post.resumen}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                                  <span>✍️ {post.autor_nombre}</span>
                                  <span>📅 {new Date(post.fecha_creacion).toLocaleDateString('es-MX')}</span>
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                    {post.categoria}
                                  </span>
                                </div>
                                <details className="mt-3">
                                  <summary className="cursor-pointer text-[#1e40af] font-medium hover:underline">
                                    Ver contenido completo
                                  </summary>
                                  <div className="mt-3 p-4 bg-gray-50 rounded max-h-64 overflow-y-auto">
                                    <pre className="whitespace-pre-wrap text-sm">{post.contenido}</pre>
                                  </div>
                                </details>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <Button
                                  onClick={() => approvePost(post.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                  size="sm"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Aprobar
                                </Button>
                                <Button
                                  onClick={() => rejectPost(post.id)}
                                  variant="destructive"
                                  size="sm"
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Rechazar
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Posts Publicados */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">
                    ✅ Posts Publicados ({publishedPosts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {publishedPosts.map((post) => (
                      <div key={post.id} className="flex justify-between items-center p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <h4 className="font-semibold text-gray-800">{post.titulo}</h4>
                          <p className="text-sm text-gray-600">
                            Por {post.autor_nombre} • {new Date(post.fecha_publicacion).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                        <Button
                          onClick={() => deletePost(post.id)}
                          variant="outline"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Empleados */}
          <TabsContent value="empleados">
            <EmpleadosTab />
          </TabsContent>

          {/* Tab: Resultados */}
          <TabsContent value="resultados">
            <ResultadosTab />
          </TabsContent>

          {/* Tab: Facturación */}
          <TabsContent value="facturacion">
            <FacturacionTab />
          </TabsContent>

          {/* Tab: CMS */}
          <TabsContent value="cms">
            <CMSTab />
          </TabsContent>

          {/* Tab: WhatsApp Analytics */}
          <TabsContent value="whatsapp">
            <WhatsAppAnalyticsTab />
          </TabsContent>

          {/* Tab: Configuración */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">🔐 Cambiar Contraseña</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium mb-2">Contraseña actual</label>
                    <Input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nueva contraseña</label>
                    <Input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirmar nueva contraseña</label>
                    <Input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      required
                    />
                  </div>
                  <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a]">
                    Actualizar Contraseña
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
